import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Compact date + time picker built from shadcn's Calendar + a native time
 * input. Kept in a single component so any page can drop it in without
 * wiring two children.
 *
 * Props:
 *   value        Date | ISO string
 *   onChange     (Date) => void
 *   testIdPrefix optional data-testid namespace
 */
export function DateTimePicker({ value, onChange, testIdPrefix = "dt" }) {
  const [open, setOpen] = useState(false);
  const asDate = value instanceof Date ? value : (value ? new Date(value) : null);
  const dateLabel = asDate
    ? asDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })
    : "Pick a date";
  const timeStr = asDate
    ? `${String(asDate.getHours()).padStart(2, "0")}:${String(asDate.getMinutes()).padStart(2, "0")}`
    : "19:00";

  const setDatePart = (d) => {
    if (!d) return;
    const merged = new Date(d);
    if (asDate) {
      merged.setHours(asDate.getHours(), asDate.getMinutes(), 0, 0);
    } else {
      const [h, m] = timeStr.split(":").map((n) => parseInt(n, 10));
      merged.setHours(h || 0, m || 0, 0, 0);
    }
    onChange(merged);
  };

  const setTimePart = (str) => {
    const [h, m] = (str || "").split(":").map((n) => parseInt(n, 10));
    const base = asDate ? new Date(asDate) : new Date();
    base.setHours(h || 0, m || 0, 0, 0);
    onChange(base);
  };

  return (
    <div className="flex gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            data-testid={`${testIdPrefix}-open`}
            className={cn(
              "flex-1 justify-start text-left bg-white/[0.04] border-white/10 hover:bg-white/[0.06]",
              !asDate && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="h-4 w-4 mr-2 text-orange-400" />
            {dateLabel}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-card border-white/10" align="start" data-testid={`${testIdPrefix}-popover`}>
          <Calendar
            mode="single"
            selected={asDate || undefined}
            onSelect={(d) => { setDatePart(d); setOpen(false); }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <div className="relative w-32">
        <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-orange-400 pointer-events-none" />
        <Input
          type="time"
          value={timeStr}
          onChange={(e) => setTimePart(e.target.value)}
          data-testid={`${testIdPrefix}-time`}
          className="pl-8 bg-white/[0.04] border-white/10 font-mono text-sm"
        />
      </div>
    </div>
  );
}
